import { useState } from 'react';
import type { ReportReason } from '@sfc/sdk';

const REASONS: { value: ReportReason; label: string }[] = [
  { value: 'spam', label: 'Spam, or the same thing posted over and over' },
  { value: 'harassment', label: 'Harassment or abuse' },
  { value: 'scam', label: 'A scam, or asking me for money' },
  { value: 'impersonation', label: 'Pretending to be somebody else' },
  { value: 'not_a_real_idea', label: 'Not a real idea' },
  { value: 'something_else', label: 'Something else' },
];

/**
 * Reporting, and blocking if there is somebody to block.
 *
 * Deliberately plain and deliberately final: it says what will happen, it
 * happens, and nothing comes back. There is no reference number and no status
 * to check later, because there is no channel between the person reporting and
 * the person reported -- which is the point of reporting rather than replying.
 *
 * Blocking asks twice. It cannot be undone from here, and a destructive thing
 * that is one tap away from a thing people tap often is a thing people do by
 * accident.
 */
export function ReportPanel({
  subject,
  onReport,
  onBlock,
  onClose,
}: {
  /** What is being reported, in words, for the heading. */
  subject: string;
  onReport: (reason: ReportReason, detail: string) => Promise<void>;
  onBlock?: () => Promise<void>;
  onClose: () => void;
}): React.JSX.Element {
  const [reason, setReason] = useState<ReportReason>();
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<'reported' | 'blocked'>();
  const [problem, setProblem] = useState<string>();
  const [confirmingBlock, setConfirmingBlock] = useState(false);

  async function run(what: () => Promise<void>, outcome: 'reported' | 'blocked') {
    setBusy(true);
    setProblem(undefined);
    try {
      await what();
      setDone(outcome);
    } catch {
      setProblem('That did not go through. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="panel" role="status" data-testid="report-done">
        <h2>{done === 'reported' ? 'Thank you' : 'Blocked'}</h2>
        <p className="fine">
          {done === 'reported'
            ? 'Somebody will read it. You will not hear back about it, and nothing you did is shown to anybody but us.'
            : 'You will not see each other again. Anything already said stays where it is.'}
        </p>
        <button className="button" type="button" onClick={onClose} data-testid="report-close">
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="panel" data-testid="report-panel">
      <h2>Report {subject}</h2>

      <fieldset className="reasons">
        <legend className="card__label">What is wrong?</legend>
        {REASONS.map((option) => (
          <label key={option.value} className="reason">
            <input
              type="radio"
              name="reason"
              value={option.value}
              checked={reason === option.value}
              onChange={() => setReason(option.value)}
            />
            {option.label}
          </label>
        ))}
      </fieldset>

      <label className="field">
        <span>Anything else we should know? (optional)</span>
        <textarea
          className="composer__box"
          rows={3}
          maxLength={1000}
          value={detail}
          onChange={(event) => setDetail(event.target.value)}
          data-testid="report-detail"
        />
      </label>

      {problem && (
        <p className="notice notice--problem" role="status">
          {problem}
        </p>
      )}

      <div className="panel__actions">
        <button
          className="button button--primary"
          type="button"
          disabled={!reason || busy}
          data-testid="report-send"
          onClick={() => reason && void run(() => onReport(reason, detail.trim()), 'reported')}
        >
          Report
        </button>
        <button className="button button--quiet" type="button" onClick={onClose}>
          Cancel
        </button>
      </div>

      {onBlock && (
        <div className="panel__block">
          {confirmingBlock ? (
            <>
              <p className="fine">
                You will not see each other again, and this cannot be undone here.
              </p>
              <div className="panel__actions">
                <button
                  className="button button--pass"
                  type="button"
                  disabled={busy}
                  data-testid="block-confirm"
                  onClick={() => void run(onBlock, 'blocked')}
                >
                  Yes, block them
                </button>
                <button
                  className="button button--quiet"
                  type="button"
                  onClick={() => setConfirmingBlock(false)}
                >
                  Keep them
                </button>
              </div>
            </>
          ) : (
            <button
              className="button button--quiet"
              type="button"
              data-testid="block"
              onClick={() => setConfirmingBlock(true)}
            >
              Block instead
            </button>
          )}
        </div>
      )}
    </div>
  );
}
